import { type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Activity,
  AlertTriangle,
  Camera,
  CheckCircle2,
  CircleDot,
  Search,
  ShieldCheck,
  ShieldQuestion,
  Terminal,
} from 'lucide-react';
import { Badge } from '@staff-app/ui';
import { cn } from '@/lib/utils';
import { formatDateTimeWithSeconds } from '@/lib/format-date';
import {
  useTroubleshootSessions,
  useTroubleshootSessionTranscript,
  type TroubleshootEvent,
  type TroubleshootSession,
} from '../hooks/useTroubleshoot';
import { ToolStepBody } from './ToolStepBody';
import { statusVariant } from './SessionsTable';

// ── helpers (mirrors scio3 DeviceTroubleshootSessionView, vendored — no shared lib with scio3) ──

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

/** `mcp__device__device__read` → `read`, `device__exec` → `exec` (the raw ids are noise to a reviewer). */
function toolKey(raw?: string): string {
  const stripped = (raw ?? '')
    .replace(/^mcp__device__/, '')
    .replace(/^mcp__[^_]+__/, '')
    .replace(/^device__/, '');
  return stripped || raw || 'tool';
}

/** Bookkeeping/KB tool steps we don't surface — the agent narrates its finding; the plumbing is noise. */
const HIDDEN_TOOLS = new Set([
  'server_record',
  'searchInternalDocs',
  'readInternalDoc',
  'searchArticles',
  'readArticle',
]);

const TOOL_ICON: Record<string, typeof Activity> = {
  read: Search,
  exec: Terminal,
  screenshot: Camera,
};

interface ParsedPayload {
  toolCallId?: string;
  title?: string;
  output?: string;
  text?: string;
  message?: string;
  command?: string;
  params?: { command?: unknown; description?: unknown };
  reason?: string;
}
function parse(ev: TroubleshootEvent): ParsedPayload {
  if (!ev.payloadJson) return {};
  try {
    return JSON.parse(ev.payloadJson) as ParsedPayload;
  } catch {
    return {};
  }
}

type TimelineItem =
  | { kind: 'message'; id: string; ts?: string | null; text: string }
  | { kind: 'user'; id: string; ts?: string | null; text: string }
  | {
      kind: 'tool';
      id: string;
      ts?: string | null;
      tool: string;
      command?: string;
      description?: string;
      output?: string;
      status: 'running' | 'ok' | 'error';
    }
  | { kind: 'auto'; id: string; ts?: string | null; command?: string; reason?: string }
  | { kind: 'ended'; id: string; ts?: string | null; text: string };

/** Fold the recorded events into a chat timeline: agent messages, user turns, and ONE row per tool call
 *  (proposal seeds it; the matching action/error by toolCallId fills in the output + status). */
function buildTimeline(events: TroubleshootEvent[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  const toolIdx = new Map<string, number>(); // toolCallId → items index (-1 = a hidden tool, skip its result)
  for (const ev of events) {
    const p = parse(ev);
    const id = `${ev.seq}`;
    switch (ev.type) {
      case 'diagnostic': {
        const text = str(p.text) ?? str(p.message);
        if (text) items.push({ kind: 'message', id, ts: ev.ts, text });
        break;
      }
      case 'proposal': {
        const tool = toolKey(str(p.title));
        if (HIDDEN_TOOLS.has(tool)) {
          if (p.toolCallId) toolIdx.set(p.toolCallId, -1);
          break;
        }
        const command = typeof p.params?.command === 'string' ? p.params.command : undefined;
        const description = str(p.params?.description)?.trim() || undefined;
        const idx = items.push({ kind: 'tool', id, ts: ev.ts, tool, command, description, status: 'running' }) - 1;
        if (p.toolCallId) toolIdx.set(p.toolCallId, idx);
        break;
      }
      case 'action':
      case 'error': {
        const at = p.toolCallId ? toolIdx.get(p.toolCallId) : undefined;
        if (at === -1) break; // result of a hidden tool
        const status: 'ok' | 'error' = ev.type === 'error' ? 'error' : 'ok';
        const target = at != null ? items[at] : undefined;
        if (target && target.kind === 'tool') {
          target.output = str(p.output);
          target.status = status;
        } else {
          items.push({ kind: 'tool', id, ts: ev.ts, tool: 'done', output: str(p.output), status });
        }
        break;
      }
      case 'user.message': {
        const text = str(p.text) ?? str(p.message);
        if (text) items.push({ kind: 'user', id, ts: ev.ts, text });
        break;
      }
      case 'approval_request': {
        items.push({ kind: 'auto', id, ts: ev.ts, command: str(p.command), reason: str(p.reason) });
        break;
      }
      case 'session.ended': {
        items.push({ kind: 'ended', id, ts: ev.ts, text: str(p.message) ?? 'Session ended' });
        break;
      }
      // session.started / approval_grant / status / verify → not rendered (noise for a read-only review)
    }
  }
  return items;
}

const tipTitle = (ts?: string | null): string | undefined =>
  ts ? formatDateTimeWithSeconds(ts) : undefined;

/**
 * The read-only transcript of one troubleshoot session, as a chat timeline — header (device/title/status +
 * meta line) + agent messages + folded tool steps + user turns. Reused by the full-page route AND the
 * list-page slide-over. View-only (no resume/start). Mirrors the customer OptiBot session view.
 */
export function TroubleshootTranscript({
  sessionId,
  session: sessionProp,
}: {
  sessionId: string;
  /** Pass the session row when the caller already has it (list / device drawer) → skips the header lookup. */
  session?: TroubleshootSession;
}) {
  // The transcript carries only events; the session row supplies the header. When the caller didn't pass
  // it (deep-link route), fetch the list to resolve it; otherwise skip that (cross-tenant) query.
  const { data: listData } = useTroubleshootSessions(undefined, { skip: !!sessionProp });
  const session =
    sessionProp ?? (listData?.staffTroubleshootSessions ?? []).find((s) => s.sessionId === sessionId);

  const { data, loading, error } = useTroubleshootSessionTranscript(sessionId);
  const events = data?.staffTroubleshootSessionTranscript ?? [];
  const timeline = buildTimeline(events);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-foreground">
            {session?.deviceName || 'Session'}
            {session?.title ? (
              <span className="text-muted-foreground">{` — ${session.title}`}</span>
            ) : null}
          </h2>
          {session ? <Badge variant={statusVariant(session.status)}>{session.status}</Badge> : null}
        </div>
        {session ? (
          <div className="mt-1 text-sm text-muted-foreground">
            {[
              session.accountId,
              session.region,
              session.mode,
              session.actor ? `Started by ${session.actor}` : null,
              session.startedAt ? formatDateTimeWithSeconds(session.startedAt) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      ) : loading && !events.length ? (
        <div className="text-sm text-muted-foreground">{'Loading transcript…'}</div>
      ) : (
        <div className="rounded-lg border bg-card">
          <div className="border-b border-border px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {'Transcript (read-only)'}
          </div>
          <ol className="space-y-3 p-4">
            {timeline.map((item) => {
              if (item.kind === 'message') return <AgentMessage key={item.id} text={item.text} ts={item.ts} />;
              if (item.kind === 'user') return <UserBubble key={item.id} text={item.text} ts={item.ts} />;
              if (item.kind === 'tool') return <ToolStep key={item.id} item={item} />;
              if (item.kind === 'auto') return <AutoApproved key={item.id} command={item.command} reason={item.reason} ts={item.ts} />;
              return <Ended key={item.id} text={item.text} ts={item.ts} />;
            })}
            {!timeline.length ? (
              <li className="text-center text-sm text-muted-foreground">{'No transcript events recorded.'}</li>
            ) : null}
          </ol>
        </div>
      )}
    </div>
  );
}

/** Compact markdown overrides for agent narration (tables/code/lists are common in the output). */
const MD_COMPONENTS = {
  p: ({ children }: { children?: ReactNode }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="mb-2 ml-4 list-disc last:mb-0">{children}</ul>
  ),
  ol: ({ children }: { children?: ReactNode }) => (
    <ol className="mb-2 ml-4 list-decimal last:mb-0">{children}</ol>
  ),
  li: ({ children }: { children?: ReactNode }) => <li className="mb-1">{children}</li>,
  code: ({ children }: { children?: ReactNode }) => (
    <code className="rounded bg-muted px-1 py-0.5">{children}</code>
  ),
  pre: ({ children }: { children?: ReactNode }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-muted p-2 last:mb-0">{children}</pre>
  ),
  table: ({ children }: { children?: ReactNode }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="min-w-full">{children}</table>
    </div>
  ),
  th: ({ children }: { children?: ReactNode }) => (
    <th className="border-b border-border px-2 py-1 text-left font-medium">{children}</th>
  ),
  td: ({ children }: { children?: ReactNode }) => (
    <td className="border-b border-border px-2 py-1">{children}</td>
  ),
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
};

/** The agent's narration — left, with an activity icon. Rendered as markdown (tables/code/lists). */
function AgentMessage({ text, ts }: { text: string; ts?: string | null }) {
  return (
    <li className="flex items-start gap-2 text-sm" title={tipTitle(ts)}>
      <Activity className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 break-words text-foreground">
        <Markdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {text}
        </Markdown>
      </div>
    </li>
  );
}

/** A human steering turn — right-aligned bubble. */
function UserBubble({ text, ts }: { text: string; ts?: string | null }) {
  return (
    <li className="flex justify-end" title={tipTitle(ts)}>
      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-lg bg-primary px-3 py-1.5 text-sm text-primary-foreground">
        {text}
      </div>
    </li>
  );
}

/** One tool call: friendly verb + command + status icon + collapsible output (result folded in place). */
function ToolStep({ item }: { item: Extract<TimelineItem, { kind: 'tool' }> }) {
  const isError = item.status === 'error';
  const isOk = item.status === 'ok';
  const Icon = isError ? AlertTriangle : isOk ? CheckCircle2 : (TOOL_ICON[item.tool] ?? ShieldQuestion);
  const output = item.output?.trim();
  return (
    <li className="flex items-start gap-2 text-sm" title={tipTitle(item.ts)}>
      <Icon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          isError ? 'text-destructive' : isOk ? 'text-muted-foreground' : 'text-primary',
        )}
      />
      <ToolStepBody
        label={item.tool}
        description={item.description}
        command={item.command}
        output={output}
        failed={isError}
      />
    </li>
  );
}

/** A device-change that was approved (auto-grant or operator) — the exact command, as an audit row. */
function AutoApproved({ command, reason, ts }: { command?: string; reason?: string; ts?: string | null }) {
  return (
    <li className="flex items-start gap-2 text-sm" title={tipTitle(ts)}>
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <span className="font-medium text-foreground">{'Approved'}</span>
        {command ? (
          <span className="ml-2 break-all font-mono text-muted-foreground">{command}</span>
        ) : null}
        {reason ? <p className="break-words text-muted-foreground">{reason}</p> : null}
      </div>
    </li>
  );
}

/** Session end marker. */
function Ended({ text, ts }: { text: string; ts?: string | null }) {
  return (
    <li
      className="flex items-center justify-center gap-1.5 py-1 text-xs text-muted-foreground"
      title={tipTitle(ts)}
    >
      <CircleDot className="h-3.5 w-3.5" />
      {text}
    </li>
  );
}
